import React, { useState, useEffect } from "react";
import { Button, Icon } from "@amzn/meridian";
import bulbTokens from "@amzn/meridian-tokens/base/icon/bulb";
import "./FlashlightButton.scss";

type FlashlightButtonProps = {
  videoRef: React.RefObject<HTMLVideoElement>;
  disabled?: boolean;
};

const FlashlightButton = ({ videoRef, disabled = false }: FlashlightButtonProps) => {
  const [torchEnabled, setTorchEnabled] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if torch is supported
  useEffect(() => {
    const checkTorchSupport = async () => {
      try {
        if (!videoRef.current?.srcObject) {
          setIsChecking(false);
          return;
        }

        const stream = videoRef.current.srcObject as MediaStream;
        const videoTrack = stream.getVideoTracks()[0];
        
        if (!videoTrack) {
          setTorchSupported(false);
          setIsChecking(false);
          return;
        }

        // Check if torch capability exists
        const capabilities = videoTrack.getCapabilities();
        const hasTorch = 'torch' in capabilities && capabilities.torch === true;
        
        setTorchSupported(hasTorch);
        setIsChecking(false);
      } catch (error) {
        console.warn("Failed to check torch support:", error);
        setTorchSupported(false);
        setIsChecking(false);
      }
    };

    // Check after a short delay to ensure video stream is ready
    const timeout = setTimeout(checkTorchSupport, 500);
    
    return () => clearTimeout(timeout);
  }, [videoRef.current?.srcObject]);

  const toggleFlashlight = async () => {
    try {
      if (!videoRef.current?.srcObject) {
        return;
      }

      const stream = videoRef.current.srcObject as MediaStream;
      const videoTrack = stream.getVideoTracks()[0];
      
      if (!videoTrack) {
        return;
      }

      const newTorchState = !torchEnabled;
      
      // Apply torch constraint
      await videoTrack.applyConstraints({
        // @ts-ignore - torch is not in TypeScript's MediaTrackConstraints type yet
        advanced: [{ torch: newTorchState }]
      });
      
      setTorchEnabled(newTorchState);
    } catch (error) {
      console.error("Failed to toggle flashlight:", error);
      // Reset state if toggle failed
      setTorchEnabled(false);
    }
  };

  // Don't render if torch is not supported or still checking
  if (isChecking || !torchSupported) {
    return null;
  }

  return (
    <div className="flashlight-button-container">
      <Button
        type="secondary"
        onClick={toggleFlashlight}
        disabled={disabled}
        className={`flashlight-button ${torchEnabled ? 'torch-active' : 'torch-inactive'}`}
        aria-label={torchEnabled ? "Turn flashlight off" : "Turn flashlight on"}
      >
        <Icon 
          tokens={bulbTokens} 
          className={`flashlight-icon ${torchEnabled ? 'icon-active' : 'icon-inactive'}`}
        />
      </Button>
    </div>
  );
};

export default FlashlightButton;